/**
 * UNA sesión = UNA fila. Este módulo fusiona las tres fuentes que antes
 * pintaban listas separadas en `/data`:
 *
 *   - `GET /know/me/performance` → `recent_sessions` (resultado de juego:
 *     tiros, goles, atajadas, score, calidad de sync). Sólo las últimas 8.
 *   - `GET /know/me/physiology/sessions` → estado de señal + ciclos de
 *     regulación por sesión (hasta 50), ya modelado por `signalsModel`.
 *   - `GET /know/responses` → el chequeo previo (`PRE-SESION`), que trae el
 *     `session_id` de la sesión a la que pertenece.
 *
 * Hasta el 2026-09-07 el jugador veía DOS tablas de sesiones (una en
 * "Sesiones recientes", otra en "Todas tus sesiones") con las mismas filas y
 * columnas distintas. Acá se unen por `id` y la vista expande cada fila.
 *
 * El id de sesión lo genera el cliente (uuid del vr-app) y viaja con el
 * casing que le puso quien la creó, así que la clave del merge se normaliza
 * en mayúsculas: la misma sesión no puede aparecer dos veces por diferir en
 * una letra.
 */

import type { PerformanceSession, ResponseSummary } from '../../api/know'
import { PRE_SESSION_CODE } from '../../api/know'
import type { SessionView } from './signalsModel'

export interface UnifiedSession {
  id: string
  startedAt: Date | null
  status: string
  /** Tiros: del bloque de performance si está, si no del de señales. */
  nTrials: number
  /** Resultado de juego — `null` cuando la sesión cayó fuera de las 8 que
   *  devuelve `recent_sessions` (sólo se sabe de ella por las señales). */
  play: PerformanceSession | null
  /** Ciclos + estado de señal — `null` si la sesión no vino en el histórico
   *  fisiológico (p. ej. quedó fuera del `limit=50`). */
  signals: SessionView | null
  /** Fecha del chequeo previo de ESA sesión, si lo respondió. */
  checkInAt: Date | null
}

function key(id: string): string {
  return id.toUpperCase()
}

export function mergeSessions(
  signals: SessionView[],
  play: PerformanceSession[],
  responses: ResponseSummary[],
): UnifiedSession[] {
  const byId = new Map<string, UnifiedSession>()

  const upsert = (id: string): UnifiedSession => {
    const k = key(id)
    let row = byId.get(k)
    if (!row) {
      row = {
        id,
        startedAt: null,
        status: '',
        nTrials: 0,
        play: null,
        signals: null,
        checkInAt: null,
      }
      byId.set(k, row)
    }
    return row
  }

  for (const s of signals) {
    const row = upsert(s.id)
    row.signals = s
    row.startedAt = s.startedAt
    row.status = s.status
    row.nTrials = s.nTrials
  }

  for (const p of play) {
    const row = upsert(p.id)
    row.play = p
    if (!row.startedAt && p.started_at) row.startedAt = new Date(p.started_at)
    if (!row.status) row.status = p.status
    // El conteo de tiros de performance manda: cuenta los trials cerrados de
    // la sesión, que es lo que el jugador reconoce como "tiros".
    row.nTrials = p.n_trials
  }

  for (const r of responses) {
    if (r.code !== PRE_SESSION_CODE || !r.session_id) continue
    const row = byId.get(key(r.session_id))
    // Un chequeo sin su sesión en ninguna de las dos listas no inventa fila:
    // sería una sesión de la que no sabemos absolutamente nada más.
    if (row) row.checkInAt = new Date(r.session_started_at ?? r.submitted_at)
  }

  return [...byId.values()].sort(
    (a, b) => (b.startedAt?.getTime() ?? 0) - (a.startedAt?.getTime() ?? 0),
  )
}
